import produce from 'immer';
import omit from 'lodash/omit';

import * as BookmarkTypes from 'common/types/reports/bookmark';
import {DisplayBookmark} from 'common/report/dashboards/types';
import {isBehaviorShowClause, isFormulaShowClause} from 'iron/common/report/insights/models/utils';

import {UI_STATE_BEHAVIOR_FIELDS, UI_STATE_METRIC_FIELDS} from '../constants';
// import {getSerializedClause} from './hydrate-bookmark';
import {isSaveableBehavior} from './behaviors';

/**
 * Returns the canonical form of a Behavior unless it's performing a comparison
 */
export function getSerializedBehaviorDefinition(
  source: BookmarkTypes.Behavior,
  opts: {forComparison?: boolean} = {},
): BookmarkTypes.Behavior {
  if (!opts.forComparison && isSaveableBehavior(source) && source.id && !source.hasUnsavedChanges) {
    return {id: source.id, type: source.type};
  }

  return omit(source, [...UI_STATE_BEHAVIOR_FIELDS, ...(opts.forComparison ? [`hasUnsavedChanges`] : [])]);
}

/**
 * Returns the canonical form of a Metric unless it's performing a comparison
 */
export function getSerializedClauseDefinition(
  source: BookmarkTypes.ShowClause,
  opts: {forComparison?: boolean} = {},
): BookmarkTypes.ShowClause {
  if (!opts.forComparison && source.id && !source.hasUnsavedChanges) {
    return {id: source.id, type: source.type};
  }

  // when comparing metrics, don't diff the automatic metric names we generate
  const comparisonFields = [`hasUnsavedChanges`, `isHidden`];
  if (isBehaviorShowClause(source) && !source.userNamed) {
    comparisonFields.push(`name`);
  }

  const clause = omit(source, [...UI_STATE_METRIC_FIELDS, ...(opts.forComparison ? comparisonFields : [])]);

  return clause;
}

/**
 * Prepares a given ShowClause for bookmark serialization.
 * This strips away any UI state populated in show clauses.
 */
export function getSerializedClause(
  clause: BookmarkTypes.ShowClause,
  opts: {forComparison?: boolean} = {},
): BookmarkTypes.ShowClause {
  clause = getSerializedClauseDefinition(clause, opts);

  /** formulas with referenced metrics need to be compared by reference when dealing with saved metrics */
  if (isFormulaShowClause(clause) && clause.referencedMetrics) {
    clause.referencedMetrics = clause.referencedMetrics.map(
      (metric) => getSerializedClauseDefinition(metric, opts) as BookmarkTypes.BehaviorShowClause,
    );
  } else if (isBehaviorShowClause(clause) && clause.behavior) {
    clause.behavior = getSerializedBehaviorDefinition(clause.behavior, opts);
  }

  return clause;
}

/**
 * Serializes a given bookmark by transforming its `sections.show` clauses
 * into their serialized form using canonical metrics.
 *
 * This also strips away all UI state from the bookmark
 */
export function serializeBookmark(bookmark: DisplayBookmark | null | undefined) {
  if (!bookmark || `sections` in bookmark.params === false) {
    return bookmark;
  }

  return produce(bookmark as DisplayBookmark<BookmarkTypes.InsightsBookmarkParams>, (draft) => {
    draft.params.sections.show = draft.params.sections.show.map((clause) => getSerializedClause(clause));
  });
}
